(() => {
  if (window.ProtekImageZoom) return;
  window.ProtekImageZoom = true;

  const stageSelector = '.protek-gallery__stage';
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const distance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

  const initStage = (stage) => {
    if (stage.dataset.protekInlineZoomReady === 'true') return;
    const image = stage.matches('.protek-gallery__stage')
      ? stage.querySelector(':scope > img')
      : stage.querySelector('img');
    if (!image) return;

    stage.dataset.protekInlineZoomReady = 'true';
    stage.classList.add('protek-inline-zoom-stage');
    image.classList.add('protek-inline-zoom-image');
    stage.setAttribute('tabindex', '0');
    stage.setAttribute('aria-label', 'Product image. Click or tap to zoom inside the image box.');

    const resetButton = document.createElement('button');
    resetButton.type = 'button';
    resetButton.className = 'protek-inline-zoom-reset';
    resetButton.setAttribute('aria-label', 'Reset product image zoom');
    resetButton.textContent = '×';
    stage.appendChild(resetButton);

    let scale = 1;
    let x = 0;
    let y = 0;
    let gesture = null;
    let moved = false;
    const pointers = new Map();

    const upgradeImage = () => {
      try {
        const source = image.currentSrc || image.src;
        const url = new URL(source, window.location.origin);
        if (/cdn\/shop\/files|cdn\.shopify\.com/.test(url.href)) {
          url.searchParams.set('width', '2000');
          image.src = url.href;
        }
      } catch (_) {}
    };

    const apply = () => {
      if (scale <= 1) {
        scale = 1;
        x = 0;
        y = 0;
      } else {
        const rect = stage.getBoundingClientRect();
        x = clamp(x, -rect.width * (scale - 1) / 2, rect.width * (scale - 1) / 2);
        y = clamp(y, -rect.height * (scale - 1) / 2, rect.height * (scale - 1) / 2);
      }
      image.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) scale(' + scale + ')';
      stage.classList.toggle('is-inline-zoomed', scale > 1);
      stage.setAttribute('aria-label', scale > 1
        ? 'Product image zoomed. Drag to inspect or press Escape to reset.'
        : 'Product image. Click or tap to zoom inside the image box.');
    };

    const reset = () => {
      scale = 1;
      x = 0;
      y = 0;
      apply();
    };
    stage.protekResetImageZoom = reset;

    const setScale = (nextScale) => {
      const wasAtRest = scale === 1;
      scale = clamp(nextScale, 1, 4);
      if (wasAtRest && scale > 1) upgradeImage();
      apply();
    };

    resetButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      reset();
      stage.focus();
    });

    stage.addEventListener('wheel', (event) => {
      event.preventDefault();
      setScale(scale + (event.deltaY < 0 ? 0.35 : -0.35));
    }, {passive: false});

    stage.addEventListener('pointerdown', (event) => {
      if (event.target === resetButton) return;
      stage.setPointerCapture?.(event.pointerId);
      pointers.set(event.pointerId, event);
      moved = false;
      stage.classList.add('is-inline-dragging');
      if (pointers.size === 1) {
        gesture = {
          startX: event.clientX,
          startY: event.clientY,
          originX: x,
          originY: y,
          targetImage: event.target === image
        };
      } else if (pointers.size === 2) {
        const pair = [...pointers.values()];
        gesture = {pinchDistance: distance(pair[0], pair[1]), startScale: scale};
      }
    });

    stage.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, event);
      if (pointers.size === 2 && gesture?.pinchDistance) {
        const pair = [...pointers.values()];
        setScale(gesture.startScale * distance(pair[0], pair[1]) / gesture.pinchDistance);
        moved = true;
      } else if (pointers.size === 1 && gesture && scale > 1) {
        const dx = event.clientX - gesture.startX;
        const dy = event.clientY - gesture.startY;
        if (Math.abs(dx) + Math.abs(dy) > 6) moved = true;
        x = gesture.originX + dx;
        y = gesture.originY + dy;
        apply();
      }
    });

    const endPointer = (event) => {
      const endingGesture = gesture;
      const wasSingle = pointers.size === 1;
      pointers.delete(event.pointerId);
      stage.classList.toggle('is-inline-dragging', pointers.size > 0);

      if (wasSingle && endingGesture?.targetImage) {
        const dx = event.clientX - endingGesture.startX;
        const dy = event.clientY - endingGesture.startY;
        if (!moved && Math.abs(dx) < 10 && Math.abs(dy) < 10) {
          scale > 1 ? reset() : setScale(2);
        }
      }

      if (pointers.size === 1) {
        const remaining = [...pointers.values()][0];
        gesture = {
          startX: remaining.clientX,
          startY: remaining.clientY,
          originX: x,
          originY: y,
          targetImage: remaining.target === image
        };
      } else if (!pointers.size) {
        gesture = null;
        moved = false;
      }
    };

    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);

    stage.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') reset();
      else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        scale > 1 ? reset() : setScale(2);
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        setScale(scale + 0.5);
      } else if (event.key === '-') {
        event.preventDefault();
        setScale(scale - 0.5);
      }
    });
  };

  const initialize = (root = document) => {
    if (root.nodeType === 1 && root.matches?.(stageSelector)) initStage(root);
    root.querySelectorAll?.(stageSelector).forEach(initStage);
  };

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-akito-thumb], [data-gallery-thumb], .protek-gallery__thumb, .thumbnail, [id^="Thumbnail-"]')) return;
    document.querySelectorAll('.protek-inline-zoom-stage.is-inline-zoomed').forEach((stage) => {
      stage.protekResetImageZoom?.();
    });
  });

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => initialize(node)));
  }).observe(document.documentElement, {childList: true, subtree: true});
})();